import * as cocoSsd from '@tensorflow-models/coco-ssd';
import '@tensorflow/tfjs';

class ObjectDetectionService {
  constructor() {
    this.model = null;
    this.isLoaded = false;
  }

  async loadModel() {
    try {
      if (this.model) return { success: true };

      console.log('Loading COCO-SSD model...');
      this.model = await cocoSsd?.load({
        base: 'lite_mobilenet_v2'
      });
      this.isLoaded = true;
      console.log('COCO-SSD model loaded successfully');
      return { success: true };
    } catch (error) {
      console.error('Model loading error:', error);
      return { success: false, error: error?.message };
    }
  }

  async detectObjects(imageElement) {
    try {
      if (!this.isLoaded) {
        const loadResult = await this.loadModel();
        if (!loadResult?.success) {
          throw new Error('Failed to load detection model');
        }
      }

      const predictions = await this.model?.detect(imageElement);
      
      // Filter and format predictions
      const detectedObjects = predictions?.filter(pred => pred?.score > 0.5)?.map(pred => ({
          class: pred?.class,
          confidence: (pred?.score * 100)?.toFixed(1),
          bbox: pred?.bbox,
          category: this.categorizeObject(pred?.class)
        }));

      return {
        success: true,
        objects: detectedObjects,
        count: detectedObjects?.length
      };
    } catch (error) {
      console.error('Object detection error:', error);
      return {
        success: false,
        error: error?.message,
        objects: []
      };
    }
  }

  categorizeObject(className) {
    // Map detected objects to financial categories
    const categoryMap = {
      'laptop': 'Electronics',
      'cell phone': 'Electronics',
      'book': 'Education',
      'bottle': 'Groceries',
      'cup': 'Dining',
      'chair': 'Furniture',
      'couch': 'Furniture',
      'tv': 'Electronics',
      'keyboard': 'Electronics',
      'mouse': 'Electronics',
      'backpack': 'Shopping',
      'handbag': 'Shopping',
      'tie': 'Shopping',
      'suitcase': 'Travel',
      'sports ball': 'Entertainment',
      'bicycle': 'Transport',
      'car': 'Transport',
      'motorcycle': 'Transport'
    };

    return categoryMap?.[className?.toLowerCase()] || 'Shopping';
  }

  estimatePrice(className, category) {
    // Basic price estimation based on object class
    const priceRanges = {
      'laptop': { min: 500, max: 2000, avg: 1000 },
      'cell phone': { min: 200, max: 1200, avg: 600 },
      'tv': { min: 300, max: 2500, avg: 800 },
      'book': { min: 10, max: 50, avg: 25 },
      'bottle': { min: 2, max: 10, avg: 5 },
      'cup': { min: 5, max: 30, avg: 15 },
      'chair': { min: 50, max: 500, avg: 150 },
      'couch': { min: 400, max: 3000, avg: 1200 },
      'backpack': { min: 30, max: 200, avg: 80 },
      'handbag': { min: 50, max: 500, avg: 150 },
      'bicycle': { min: 200, max: 2000, avg: 600 },
      'car': { min: 15000, max: 80000, avg: 35000 },
      'motorcycle': { min: 3000, max: 20000, avg: 8000 }
    };

    const range = priceRanges?.[className?.toLowerCase()];
    if (range) {
      return {
        estimated: range?.avg,
        min: range?.min,
        max: range?.max
      };
    }

    // Default estimation based on category
    const categoryDefaults = {
      'Electronics': 500,
      'Furniture': 300,
      'Shopping': 100,
      'Groceries': 20,
      'Dining': 30,
      'Transport': 5000,
      'Entertainment': 50,
      'Education': 40,
      'Travel': 500
    };

    return {
      estimated: categoryDefaults?.[category] || 100,
      min: (categoryDefaults?.[category] || 100) * 0.5,
      max: (categoryDefaults?.[category] || 100) * 2
    };
  }

  async detectFromVideo(videoElement) {
    try {
      if (!this.isLoaded) {
        await this.loadModel();
      }

      // Create canvas to capture video frame
      const canvas = document.createElement('canvas');
      canvas.width = videoElement?.videoWidth;
      canvas.height = videoElement?.videoHeight;
      const ctx = canvas?.getContext('2d');
      ctx?.drawImage(videoElement, 0, 0);

      return await this.detectObjects(canvas);
    } catch (error) {
      console.error('Video detection error:', error);
      return {
        success: false,
        error: error?.message,
        objects: []
      };
    }
  }

  dispose() {
    if (this.model) {
      this.model?.dispose();
      this.model = null;
      this.isLoaded = false;
    }
  }
}

export default ObjectDetectionService;
